import { Injectable, computed, effect, inject, signal } from '@angular/core';
import {
  SwUpdate,
  type NoNewVersionDetectedEvent,
  type UnrecoverableStateEvent,
  type VersionEvent,
  type VersionReadyEvent
} from '@angular/service-worker';
import { BusyStateService } from './busy-state';
import { LogService } from '../log.service';
import { AppVersionService } from '@core/version/app-version.service';

export type UpdateLifecycleStatus =
  | 'idle'
  | 'ready'
  | 'blocked'
  | 'applying'
  | 'failed'
  | 'unrecoverable';

interface VersionFailedEvent {
  type: 'VERSION_FAILED';
  error: unknown;
  version: { hash: string };
}

type SupportedVersionEvent = VersionEvent | VersionFailedEvent;

@Injectable({ providedIn: 'root' })
export class UpdateManagerService {
  private static readonly UPDATE_CHECK_INTERVAL_MS = 6 * 60 * 60 * 1000;
  private static readonly UPDATE_RELOAD_TIMEOUT_MS = 1500;
  private static readonly BYPASS_QUERY_PARAM = 'ngsw-bypass';
  private static readonly UPDATE_QUERY_PARAM = 'pwa-update';
  private static readonly RECOVERY_QUERY_PARAM = 'pwa-recovery';
  private static readonly RECOVERY_SESSION_KEY = 'pwa-update-recovery';

  private readonly swUpdate = inject(SwUpdate);
  private readonly busyState = inject(BusyStateService);
  private readonly logger = inject(LogService);
  private readonly appVersion = inject(AppVersionService);

  private readonly _status = signal<UpdateLifecycleStatus>('idle');
  private readonly _errorMessage = signal<string | null>(null);
  private readonly lastCheckAtSignal = signal<number | null>(null);
  private readonly bannerDismissed = signal(false);
  private readonly latestReadyEvent = signal<VersionReadyEvent | null>(null);

  readonly pendingUpdate = signal(false);
  readonly currentVersionHash = signal<string | null>(null);
  readonly latestVersionHash = signal<string | null>(null);
  readonly latestVersionLabel = signal<string | null>(null);
  readonly currentVersionHashShort = computed(() => this.shortenHash(this.currentVersionHash()));
  readonly latestVersionHashShort = computed(() => this.shortenHash(this.latestVersionHash()));

  private isChecking = false;
  private isApplying = false;
  private isRecovering = false;
  private initialized = false;

  readonly status = this._status.asReadonly();
  readonly errorMessage = this._errorMessage.asReadonly();
  readonly lastCheckAt = this.lastCheckAtSignal.asReadonly();
  readonly blockingReasons = this.busyState.blockingReasons;
  readonly canApply = computed(
    () => !this.isApplying && !this.isRecovering && this.busyState.isSafeToReload()
  );
  readonly showBanner = computed(() => {
    const status = this.status();
    if (status === 'failed' || status === 'unrecoverable') {
      return true;
    }

    if (!this.pendingUpdate()) {
      return false;
    }

    if (this.busyState.isSafeToReload() || status === 'applying') {
      return false;
    }

    return !this.bannerDismissed();
  });
  readonly updateAvailableUI = computed(() => this.showBanner());

  private readonly handleVisibilityChange = () => {
    if (typeof document === 'undefined') {
      return;
    }
    if (document.visibilityState === 'visible') {
      void this.checkForUpdate();
    }
  };

  private readonly handleOnline = () => {
    void this.checkForUpdate();
  };

  constructor() {
    effect(() => {
      const readyEvent = this.latestReadyEvent();
      const status = this.status();

      if (status === 'unrecoverable' || status === 'failed' || status === 'applying') {
        return;
      }

      if (!this.pendingUpdate() || !readyEvent) {
        return;
      }

      if (this.busyState.isSafeToReload()) {
        if (status === 'ready') {
          return;
        }

        this._status.set('ready');
        void this.applyUpdate();
        return;
      }

      if (status !== 'blocked') {
        this._status.set('blocked');
      }
    });
  }

  init(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    this.cleanupUpdateReloadUrl();

    if (!this.swUpdate.isEnabled) {
      return;
    }

    this.swUpdate.versionUpdates.subscribe((event: SupportedVersionEvent) => {
      switch (event.type) {
        case 'VERSION_READY':
          this.clearRecoveryAttempt();
          this.latestReadyEvent.set(event);
          this.currentVersionHash.set(event.currentVersion.hash);
          this.latestVersionHash.set(event.latestVersion.hash);
          void this.loadLatestVersionLabel();
          this.pendingUpdate.set(true);
          this.bannerDismissed.set(false);
          this._status.set('blocked');
          this._errorMessage.set(null);
          return;
        case 'VERSION_INSTALLATION_FAILED':
          this.handleFailedUpdate(
            '[UpdateManager] Falló la instalación de la actualización',
            event.error,
            event.version.hash
          );
          return;
        case 'VERSION_FAILED':
          this.handleFailedUpdate(
            '[UpdateManager] La versión activa quedó en estado fallido',
            event.error,
            event.version.hash
          );
          return;
        case 'NO_NEW_VERSION_DETECTED':
          this.handleNoNewVersionDetected(event);
          return;
        default:
          return;
      }
    });

    this.swUpdate.unrecoverable.subscribe((event: UnrecoverableStateEvent) => {
      this.pendingUpdate.set(false);
      this.latestReadyEvent.set(null);
      this.bannerDismissed.set(false);
      this._status.set('unrecoverable');
      this._errorMessage.set(event.reason);
      this.logger.error('[UpdateManager] Estado irrecuperable del service worker', {
        reason: event.reason
      });

      if (this.busyState.isSafeToReload()) {
        void this.forceRecoverFromBrokenUpdate();
      }
    });

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.handleOnline);
    }

    queueMicrotask(() => {
      void this.checkForUpdate();
      if (typeof window !== 'undefined') {
        window.setInterval(() => {
          void this.checkForUpdate();
        }, UpdateManagerService.UPDATE_CHECK_INTERVAL_MS);
      }
    });
  }

  async applyUpdate(): Promise<void> {
    if (
      !this.swUpdate.isEnabled ||
      this.isApplying ||
      this.isRecovering ||
      !this.busyState.isSafeToReload()
    ) {
      return;
    }

    if (this.status() === 'failed' || this.status() === 'unrecoverable') {
      await this.forceRecoverFromBrokenUpdate(true);
      return;
    }

    if (!this.latestReadyEvent()) {
      return;
    }

    try {
      this.isApplying = true;
      this._status.set('applying');
      await this.swUpdate.activateUpdate();
      await this.waitForControllerChange();
      this.clearPendingUpdateState();
      this.reloadWithServiceWorkerBypass();
    } catch (error) {
      this.logger.error('[UpdateManager] Error al activar actualización', {
        error: error instanceof Error ? error.message : String(error)
      });
      this.pendingUpdate.set(true);
      this.bannerDismissed.set(false);
      this._status.set('failed');
      this._errorMessage.set(this.stringifyError(error));
    } finally {
      this.isApplying = false;
    }
  }

  dismiss(): void {
    this.bannerDismissed.set(true);
  }

  private async checkForUpdate(): Promise<void> {
    if (!this.swUpdate.isEnabled || this.isChecking) {
      return;
    }
    this.isChecking = true;
    try {
      this.lastCheckAtSignal.set(Date.now());
      await this.swUpdate.checkForUpdate();
    } catch (error) {
      this.logger.warn('[UpdateManager] checkForUpdate falló', {
        error: error instanceof Error ? error.message : String(error)
      });
    } finally {
      this.isChecking = false;
    }
  }

  private handleFailedUpdate(message: string, error: unknown, versionHash?: string): void {
    this.pendingUpdate.set(false);
    this.latestReadyEvent.set(null);
    this.bannerDismissed.set(false);
    this._status.set('failed');
    this._errorMessage.set(this.stringifyError(error));
    this.latestVersionHash.set(versionHash ?? this.latestVersionHash());
    this.logger.error(message, {
      error: this.stringifyError(error)
    });

    if (this.busyState.isSafeToReload()) {
      void this.forceRecoverFromBrokenUpdate();
    }
  }

  private handleNoNewVersionDetected(_event: NoNewVersionDetectedEvent): void {
    if (this.pendingUpdate()) {
      return;
    }

    if (this.status() !== 'failed' && this.status() !== 'unrecoverable') {
      this._status.set('idle');
    }

    this.clearRecoveryAttempt();
  }

  private stringifyError(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }

    return String(error);
  }

  private clearPendingUpdateState(): void {
    this.pendingUpdate.set(false);
    this.latestReadyEvent.set(null);
    this.bannerDismissed.set(false);
    this._errorMessage.set(null);
  }

  private reloadWithServiceWorkerBypass(): void {
    location.replace(this.buildUpdateReloadUrl());
  }

  private buildUpdateReloadUrl(): string {
    const url = new URL(location.href);
    url.searchParams.set(UpdateManagerService.BYPASS_QUERY_PARAM, 'true');
    url.searchParams.set(
      UpdateManagerService.UPDATE_QUERY_PARAM,
      this.latestVersionHash() ?? 'unrecoverable'
    );
    url.searchParams.set(UpdateManagerService.RECOVERY_QUERY_PARAM, Date.now().toString());

    return url.toString();
  }

  private cleanupUpdateReloadUrl(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const url = new URL(window.location.href);
    const removedBypass = url.searchParams.has(UpdateManagerService.BYPASS_QUERY_PARAM);
    const removedUpdate = url.searchParams.has(UpdateManagerService.UPDATE_QUERY_PARAM);
    const removedRecovery = url.searchParams.has(UpdateManagerService.RECOVERY_QUERY_PARAM);

    url.searchParams.delete(UpdateManagerService.BYPASS_QUERY_PARAM);
    url.searchParams.delete(UpdateManagerService.UPDATE_QUERY_PARAM);
    url.searchParams.delete(UpdateManagerService.RECOVERY_QUERY_PARAM);

    if (!removedBypass && !removedUpdate && !removedRecovery) {
      return;
    }

    const nextUrl = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState(window.history.state, document.title, nextUrl);
  }

  private async waitForControllerChange(): Promise<void> {
    if (typeof navigator === 'undefined' || !navigator.serviceWorker?.controller) {
      return;
    }

    await new Promise<void>(resolve => {
      const serviceWorker = navigator.serviceWorker;
      const finish = () => {
        window.clearTimeout(timeoutId);
        serviceWorker.removeEventListener('controllerchange', finish);
        resolve();
      };
      const timeoutId = window.setTimeout(finish, UpdateManagerService.UPDATE_RELOAD_TIMEOUT_MS);

      serviceWorker.addEventListener('controllerchange', finish, { once: true });
    });
  }

  private async loadLatestVersionLabel(): Promise<void> {
    try {
      const versionInfo = await this.appVersion.loadVersionInfo({ bypassServiceWorker: true });
      this.latestVersionLabel.set(versionInfo.version);
    } catch (error) {
      this.latestVersionLabel.set(null);
      this.logger.warn('[UpdateManager] No se pudo resolver la version nueva', {
        error: error instanceof Error ? error.message : String(error)
      });
    }
  }

  private shortenHash(hash: string | null): string | null {
    return hash ? hash.slice(0, 7) : null;
  }

  private async forceRecoverFromBrokenUpdate(force = false): Promise<void> {
    if (this.isRecovering) {
      return;
    }

    this.isRecovering = true;
    const recoveryHash = this.latestVersionHash() ?? 'unrecoverable';

    try {
      if (!force && this.hasRecoveryAttempt(recoveryHash)) {
        this.logger.warn('[UpdateManager] Se evitó un loop de recuperación para la misma versión', {
          hash: recoveryHash
        });
        return;
      }

      this.storeRecoveryAttempt(recoveryHash);
      this.clearPendingUpdateState();

      try {
        await Promise.all([
          this.unregisterServiceWorkers(),
          this.clearAngularServiceWorkerCaches()
        ]);
      } catch (error) {
        this.logger.warn(
          '[UpdateManager] La recuperación forzada encontró errores al limpiar el service worker',
          {
            error: this.stringifyError(error)
          }
        );
      }

      this.reloadWithServiceWorkerBypass();
    } finally {
      this.isRecovering = false;
    }
  }

  private async unregisterServiceWorkers(): Promise<void> {
    if (
      typeof navigator === 'undefined' ||
      typeof window === 'undefined' ||
      !navigator.serviceWorker?.getRegistrations
    ) {
      return;
    }

    const registrations = await navigator.serviceWorker.getRegistrations();
    const angularRegistrations = registrations.filter(registration =>
      this.isCurrentAppAngularServiceWorkerRegistration(registration)
    );

    await Promise.all(angularRegistrations.map(registration => registration.unregister()));
  }

  private isCurrentAppAngularServiceWorkerRegistration(
    registration: ServiceWorkerRegistration
  ): boolean {
    const workerScriptUrl =
      registration.active?.scriptURL ??
      registration.waiting?.scriptURL ??
      registration.installing?.scriptURL;

    if (!workerScriptUrl || !workerScriptUrl.includes('ngsw-worker.js')) {
      return false;
    }

    const appScope = new URL('./', window.location.href).href;
    return registration.scope === appScope || registration.scope.startsWith(appScope);
  }

  private async clearAngularServiceWorkerCaches(): Promise<void> {
    if (typeof caches === 'undefined') {
      return;
    }

    const cacheKeys = await caches.keys();
    const angularCacheKeys = cacheKeys.filter(cacheKey => cacheKey.startsWith('ngsw:'));
    await Promise.all(angularCacheKeys.map(cacheKey => caches.delete(cacheKey)));
  }

  private hasRecoveryAttempt(hash: string): boolean {
    if (typeof sessionStorage === 'undefined') {
      return false;
    }

    return sessionStorage.getItem(UpdateManagerService.RECOVERY_SESSION_KEY) === hash;
  }

  private storeRecoveryAttempt(hash: string): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }

    sessionStorage.setItem(UpdateManagerService.RECOVERY_SESSION_KEY, hash);
  }

  private clearRecoveryAttempt(): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }

    sessionStorage.removeItem(UpdateManagerService.RECOVERY_SESSION_KEY);
  }
}
